/**
 * One exercise on its own card (course lesson, daily review, placement, challenge): the text to
 * read or the recording to hear (en-GB voice, the script stays hidden until the answer), the
 * answer control, « Check », then the correction at once with its explanation, the French help
 * and a word of encouragement. « speak » records the learner and sends the recording.
 */

import { type Feedback, type Item, type ItemResponse, answerText, initialResponse, isEmptyResponse, splitModelAnswer, tfngLabel } from "@agencyos/core";
import { Button } from "@agencyos/ui-web";
import { CheckCircle2, Headphones, Languages, Mic, Play, RotateCcw, Square, XCircle } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { closeStream, openMicrophone, recorderMimeType, speechAvailable, stopSpeaking } from "../audio";
import { clock } from "../text";
import { useContentLang } from "./contentLang";
import { InMyLanguage } from "./InMyLanguage";
import { ItemInput } from "./ItemInput";
import { speakEnglish } from "./speech";
import { speakDialogue } from "./voice";

const PRAISE = ["p1", "p2", "p3", "p4"];
const COMFORT = ["c1", "c2", "c3"];

/** A stable word of encouragement for an exercise (never random between renders). */
function cheer(id: string, correct: boolean): string {
  const list = correct ? PRAISE : COMFORT;
  const sum = [...id].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return list[sum % list.length] ?? list[0] ?? "p1";
}

/** « Listen » for a script read by the English voice; « Stop » while it plays, then heard again
 * at will (D-144), unless `once`. */
export function ListenScript({ script, once = false, onHeard }: { script: string; once?: boolean; onHeard?: () => void }) {
  const { t } = useTranslation();
  const [state, setState] = useState<"idle" | "playing" | "done">("idle");
  const lang = useContentLang();
  useEffect(() => () => stopSpeaking(), []);
  const play = async () => {
    setState("playing");
    // TCF (D-107): fr-FR, one voice per speaker, the labels never read aloud
    await (lang === "fr" ? speakDialogue(script, "fr-FR") : speakEnglish(script));
    setState("done");
    onHeard?.();
  };
  if (!speechAvailable()) return <p className="ac-footnote">{t("ielts.listen.noVoice")}</p>;
  return (
    <div className={`ac-listen ac-listen--${state === "playing" ? "playing" : state} ac-listen--compact`}>
      {state === "playing" ? (
        <>
          <p className="ac-status" role="status">
            <span className="ac-wave ac-wave--small" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((n) => (
                <span key={n} style={{ animationDelay: `${n * -0.15}s` }} />
              ))}
            </span>
            {t("ielts.listen.playing")}
          </p>
          {!once && (
            <Button variant="secondary" icon={<Square aria-hidden size={14} />} onClick={() => stopSpeaking()}>
              {t("listening.training.stop")}
            </Button>
          )}
        </>
      ) : state === "done" && once ? (
        <p className="ac-status ac-status--success">
          <Headphones aria-hidden size={16} />
          {t("ielts.listen.heard")}
        </p>
      ) : (
        <Button variant="tinted" icon={<Play aria-hidden size={16} />} onClick={() => void play()}>
          {state === "done" ? t("ielts.listen.again") : t("ielts.listen.play")}
        </Button>
      )}
    </div>
  );
}

/** The correction of one answer. */
export function FeedbackBox({ item, feedback, translated = "" }: { item: Item; feedback: Feedback; translated?: string }) {
  const { t } = useTranslation();
  const lang = useContentLang();
  const [french, setFrench] = useState(false);
  const [english, setEnglish] = useState(false);
  const model = splitModelAnswer(feedback.modelAnswer);
  const production = item.type === "write" || item.type === "speak";
  const correct = feedback.correct === true;
  const partial = !correct && feedback.points !== null && feedback.marks !== null && feedback.points > 0;
  const raw = feedback.answer !== null && feedback.answer !== undefined ? answerText(item, feedback.answer) : "";
  const expected = item.type === "tfng" ? tfngLabel(raw, lang) : raw;
  return (
    <div
      className={`ac-correction${production ? " ac-correction--info" : correct ? " ac-correction--ok" : ""}`}
      role="status"
    >
      {production ? (
        <p className="ac-status">
          <CheckCircle2 aria-hidden size={18} />
          <strong>{t("ielts.feedback.sent")}</strong>
        </p>
      ) : (
        <p className="ac-status">
          {correct ? <CheckCircle2 aria-hidden size={18} /> : <XCircle aria-hidden size={18} />}
          <strong>{correct ? t("ielts.feedback.right") : partial ? t("ielts.feedback.partial", { points: feedback.points, marks: feedback.marks }) : t("ielts.feedback.wrong")}</strong>
          <span className="ac-secondary"> · {t(`ielts.feedback.cheer.${cheer(item.id, correct)}`)}</span>
        </p>
      )}
      {production && <p className="ac-secondary">{lang === "fr" ? t("academyCourse.tcf.feedback.sentText") : t("ielts.feedback.sentText")}</p>}
      {feedback.wordWarning && <p className="ac-note ac-note--warning">{feedback.wordWarning}</p>}
      {!production && !correct && expected && (
        <p>
          <span className="ac-label">{t("ielts.feedback.answer")}</span>{" "}
          <strong lang={lang}>{expected}</strong>
        </p>
      )}
      {feedback.explanation && (
        <p lang={lang} className="ac-correction__why">
          {feedback.explanation}
        </p>
      )}
      {/* D-122: why it is right or wrong, in the mother tongue (when the lesson's own translation has none) */}
      {feedback.explanation && !translated && <InMyLanguage text={feedback.explanation} source={lang} examLanguage={lang} kind="feedback" />}
      {translated && (
        <div className="ac-help-fr ac-translated">
          <span className="ac-badge-ai">{t("academyCourse.translate.badge")}</span>
          <p>{translated}</p>
        </div>
      )}
      {feedback.helpEn && (
        <div className="ac-help-fr">
          <Button variant="ghost" size="sm" icon={<Languages aria-hidden size={15} />} aria-expanded={english} onClick={() => setEnglish(!english)}>
            {english ? t("academyCourse.lesson.hideEnglish") : t("academyCourse.lesson.showEnglish")}
          </Button>
          {english && <p lang="en">{feedback.helpEn}</p>}
        </div>
      )}
      {feedback.helpFr && (
        <div className="ac-help-fr">
          <Button variant="ghost" size="sm" icon={<Languages aria-hidden size={15} />} aria-expanded={french} onClick={() => setFrench(!french)}>
            {french ? t("ielts.lesson.hideFrench") : t("ielts.lesson.showFrench")}
          </Button>
          {french && <p lang="fr">{feedback.helpFr}</p>}
        </div>
      )}
      {production && feedback.modelAnswer && (
        <details>
          <summary>{t("ielts.feedback.model")}</summary>
          <p className="ac-production-text" lang={lang}>
            {model.answer}
          </p>
          {model.annotation && (
            <div className="ac-annotation">
              <p className="ac-label">{t("academyCourse.lesson.annotation")}</p>
              <p lang={lang}>{model.annotation}</p>
            </div>
          )}
        </details>
      )}
    </div>
  );
}

type RecState = "idle" | "recording" | "sending" | "error";

/** « speak »: record up to the allowed time, listen back, then send. */
function SpeakRecorder({ seconds, onSend }: { seconds: number; onSend: (audio: Blob) => Promise<void> }) {
  const { t } = useTranslation();
  const [state, setState] = useState<RecState>("idle");
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState("");
  const [left, setLeft] = useState(seconds);
  const [denied, setDenied] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      window.clearInterval(timer.current);
      if (recorder.current?.state === "recording") recorder.current.stop();
      closeStream(stream.current);
    },
    [],
  );
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);

  const stop = () => {
    window.clearInterval(timer.current);
    if (recorder.current?.state === "recording") recorder.current.stop();
  };

  const start = async () => {
    setDenied(false);
    try {
      stream.current = stream.current ?? (await openMicrophone());
    } catch {
      setDenied(true);
      return;
    }
    const mimeType = recorderMimeType();
    const media = new MediaRecorder(stream.current, mimeType ? { mimeType } : undefined);
    const chunks: Blob[] = [];
    media.ondataavailable = (event) => {
      if (event.data.size) chunks.push(event.data);
    };
    media.onstop = () => {
      const recorded = new Blob(chunks, { type: media.mimeType || mimeType || "audio/webm" });
      setBlob(recorded);
      setUrl(URL.createObjectURL(recorded));
      setState("idle");
    };
    recorder.current = media;
    media.start(1000);
    setState("recording");
    const began = Date.now();
    setLeft(seconds);
    timer.current = window.setInterval(() => {
      const rest = Math.max(0, seconds - Math.floor((Date.now() - began) / 1000));
      setLeft(rest);
      if (rest === 0) stop();
    }, 250);
  };

  const send = async () => {
    if (!blob) return;
    setState("sending");
    try {
      await onSend(blob);
    } catch {
      setState("error");
    }
  };

  if (typeof MediaRecorder === "undefined") return <p className="ko">{t("academy.speaking.noRecorder")}</p>;
  return (
    <div className="ac-speak">
      {state === "recording" ? (
        <>
          <p className="ac-status" role="status">
            <span className="ac-rec-dot" aria-hidden="true" />
            {t("academy.speaking.recording")} · <span className="ao-num">{clock(left)}</span>
          </p>
          <Button variant="secondary" icon={<Square aria-hidden size={14} />} onClick={stop}>
            {t("ielts.speak.stop")}
          </Button>
        </>
      ) : (
        <>
          {url && (
            // The learner's own recording: nothing to caption.
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio controls src={url} preload="metadata" aria-label={t("ielts.speak.listenBack")} />
          )}
          <div className="ac-card__actions ac-card__actions--start">
            <Button variant={blob ? "ghost" : "tinted"} icon={blob ? <RotateCcw aria-hidden size={15} /> : <Mic aria-hidden size={16} />} onClick={() => void start()} disabled={state === "sending"}>
              {blob ? t("ielts.speak.again") : t("ielts.speak.record", { time: clock(seconds) })}
            </Button>
          </div>
          {blob && (
            <Button onClick={() => void send()} disabled={state === "sending"}>
              {state === "sending" ? t("common.loading") : t("ielts.speak.send")}
            </Button>
          )}
        </>
      )}
      {denied && (
        <p className="ko" role="alert">
          {t("academy.speaking.micDenied")}
        </p>
      )}
      {state === "error" && (
        <p className="ko" role="alert">
          {t("academy.speaking.uploadError")}
        </p>
      )}
    </div>
  );
}

export interface ExerciseCardProps {
  item: Item;
  /** Answered before (a lesson reopened): shown with its correction. */
  done?: { response: ItemResponse; feedback: Feedback } | null;
  onCheck: (response: ItemResponse) => Promise<Feedback | null>;
  onRecording?: (audio: Blob) => Promise<Feedback | null>;
  /** Counter above the prompt (« Exercise 3 of 8 »). */
  counter?: string;
  /** No correction (placement, challenge): the answer is only saved. */
  silent?: boolean;
  checkLabel?: string;
  /** The explanation of the correction in the learner's mother tongue (AI, D-107). */
  translated?: string;
}

export function ExerciseCard({ item, done = null, onCheck, onRecording, counter, silent = false, checkLabel, translated = "" }: ExerciseCardProps) {
  const lang = useContentLang();
  const { t } = useTranslation();
  const titleId = useId();
  const [value, setValue] = useState<ItemResponse>(() => done?.response ?? initialResponse(item));
  const [feedback, setFeedback] = useState<Feedback | null>(done?.feedback ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  // Without a voice on this device the exercise stays usable (the script is not read).
  const [heard, setHeard] = useState(!item.script || !speechAvailable());
  const answered = feedback !== null;
  const inline = item.type === "gap";

  const check = async () => {
    setBusy(true);
    setError(false);
    try {
      const result = await onCheck(value);
      if (!silent) setFeedback(result);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  const sendRecording = async (audio: Blob) => {
    if (!onRecording) return;
    const result = await onRecording(audio);
    setFeedback(result);
  };

  return (
    <article className="ac-item" aria-labelledby={titleId}>
      {counter && <p className="ac-player__eyebrow ao-num">{counter}</p>}
      {item.instructions && (
        <p className="ac-item__instructions" lang={lang}>
          {item.instructions}
        </p>
      )}
      {/* D-122: the instruction in the mother tongue, when learning (never in a silent exam) */}
      {item.instructions && !silent && <InMyLanguage text={item.instructions} source={lang} examLanguage={lang} kind="instruction" />}
      {item.text && (
        <div className="ac-document ac-document--reading" lang={lang}>
          {item.text}
        </div>
      )}
      {item.context && <p className="ac-item__context">{item.context}</p>}
      {item.script && (
        <div className="ac-item__listen">
          <ListenScript script={item.script} onHeard={() => setHeard(true)} />
          {answered && (
            <details className="ac-item__script">
              <summary>{t("ielts.listen.transcript")}</summary>
              <p className="ac-production-text" lang={lang}>
                {item.script}
              </p>
            </details>
          )}
        </div>
      )}
      {inline ? (
        <h3 id={titleId} className="visually-hidden">
          {t("ielts.item.gapTitle")}
        </h3>
      ) : (
        <h3 id={titleId} className="ac-stem" lang={lang}>
          {item.prompt}
        </h3>
      )}
      {item.type === "speak" ? (
        answered ? null : (
          <SpeakRecorder seconds={item.seconds ?? 60} onSend={sendRecording} />
        )
      ) : (
        <ItemInput item={item} value={value} onChange={setValue} disabled={answered || busy || !heard} feedback={silent ? null : feedback} labelledBy={titleId} />
      )}
      {!heard && <p className="ac-footnote">{t("ielts.listen.first")}</p>}
      {feedback && !silent && <FeedbackBox item={item} feedback={feedback} translated={translated} />}
      {error && (
        <p className="ko" role="alert">
          {t("academy.player.networkError")}
        </p>
      )}
      {!answered && item.type !== "speak" && (
        <div className="ac-question__actions">
          <Button size="lg" onClick={() => void check()} disabled={busy || isEmptyResponse(item, value)}>
            {busy ? t("common.loading") : (checkLabel ?? (item.type === "write" ? t("ielts.feedback.send") : t("ielts.feedback.check")))}
          </Button>
        </div>
      )}
    </article>
  );
}
